import { cn } from '@/lib/utils';
import { MessageContext } from 'contexts/MessageContext';
import { memo, useContext } from 'react';

import {
  type IAction,
  type IMessageElement,
  type IStep
} from '@chainlit/react-client';

import { useLayoutMaxWidth } from 'hooks/useLayoutMaxWidth';

import { Messages } from '..';
import { AskActionButtons } from './AskActionButtons';
import { AskFileButton } from './AskFileButton';
import { MessageAvatar } from './Avatar';
import { MessageButtons } from './Buttons';
import { MessageContent } from './Content';
import Step from './Step';
import UserMessage from './UserMessage';

interface Props {
  message: IStep;
  elements: IMessageElement[];
  actions: IAction[];
  indent: number;
  isRunning?: boolean;
  isScorable?: boolean;
  scorableRun?: IStep;
}

const Message = memo(
  ({
    message,
    elements,
    actions,
    isRunning,
    indent,
    isScorable,
    scorableRun
  }: Props) => {
    const { allowHtml, cot, latex, onError } = useContext(MessageContext);
    const layoutMaxWidth = useLayoutMaxWidth();
    const isAsk = message.waitForAnswer;
    const isUserMessage = message.type === 'user_message';
    const isStep = !message.type.includes('message');
    
    // Only keep tool calls if Chain of Thought is tool_call
    const toolCallSkip =
      isStep && cot === 'tool_call' && message.type !== 'tool';

    const hiddenSkip = isStep && cot === 'hidden';

    const skip = toolCallSkip || hiddenSkip;

    const formatTimestamp = (createdAt?: string | number) => {
      if (!createdAt) return null;
      
      // Handle both number (milliseconds) and string (ISO format)
      const date = typeof createdAt === 'number' 
        ? new Date(createdAt) 
        : new Date(createdAt);
      
      if (isNaN(date.getTime())) return null;
      
      // This automatically converts to user's local timezone
      // const month = String(date.getMonth() + 1).padStart(2, '0');
      // const day = String(date.getDate()).padStart(2, '0');
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12; // Convert to 12-hour format
      // const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${hours}:${minutes} ${ampm}`;
    };

    const getDisplayTimestamp = (message: IStep) => {
      // For assistant messages, check completedAt in metadata first
      if (message.type !== 'user_message' && message.metadata?.completedAt) {
        return message.metadata.completedAt;
      }
      
      // For user messages, check createdAt in metadata
      if (message.metadata?.createdAt) {
        return message.metadata.createdAt;
      }
      
      // Fallback to message.createdAt (Chainlit's default timestamp)
      if (message.createdAt) {
        return message.createdAt;
      }
      
      return null;
    };

    if (skip) {
      if (!message.steps) {
        return null;
      }
      return (
        <Messages
          messages={message.steps}
          elements={elements}
          actions={actions}
          indent={indent}
          isRunning={isRunning}
          scorableRun={scorableRun}
        />
      );
    }

    return (
      <>
        <div className="step my-2">
          <div
            className="flex flex-col"
            style={{
              maxWidth: indent ? '100%' : layoutMaxWidth
            }}
          >
            <div
              className={cn('flex flex-grow pb-2')}
              id={`step-${message.id}`}
            >
              {/* User message is displayed differently */}
              {isUserMessage ? (
                <div className="flex flex-col flex-grow max-w-full">
                  <UserMessage message={message} elements={elements}>
                    <MessageContent
                      elements={[]}
                      message={message}
                      allowHtml={allowHtml}
                      latex={latex}
                    />
                  </UserMessage>
                  {/* ✅ Show timestamp immediately for user messages (don't wait for isRunning) */}
                  {getDisplayTimestamp(message) && (
                    <div className="text-[10px] text-muted-foreground/60 mt-1 ml-auto mr-2">
                      {formatTimestamp(getDisplayTimestamp(message))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="ai-message flex gap-4 w-full">
                  {!isStep || !indent ? (
                    <MessageAvatar author={message.name} />
                  ) : null}
                  {/* Display the step and its children */}
                  {isStep ? (
                    <Step step={message} isRunning={isRunning}>
                      {message.steps ? (
                        <Messages
                          messages={message.steps.filter(
                            (s) => !s.type.includes('message')
                          )}
                          elements={elements}
                          actions={actions}
                          indent={indent + 1}
                          isRunning={isRunning}
                        />
                      ) : null}
                      <MessageContent
                        elements={elements}
                        message={message}
                        allowHtml={allowHtml}
                        latex={latex}
                      />
                      <MessageButtons message={message} actions={actions} />
                      {/* ✅ Use getDisplayTimestamp instead of message.createdAt */}
                      {!isRunning && getDisplayTimestamp(message) && (
                        <div className="text-[10px] text-muted-foreground/60 mt-2">
                          {formatTimestamp(getDisplayTimestamp(message))}
                        </div>
                      )}
                    </Step>
                  ) : (
                    // Display an assistant message
                    <div className="flex flex-col items-start min-w-[150px] flex-grow gap-2">
                      <MessageContent
                        elements={elements}
                        message={message}
                        allowHtml={allowHtml}
                        latex={latex}
                      />
                      {!isRunning && isAsk ? (
                        <>
                          <AskFileButton onError={onError} />
                          <AskActionButtons
                            actions={actions}
                            messageId={message.id}
                          />
                        </>
                      ) : null}
                      <MessageButtons
                        message={message}
                        actions={actions}
                        run={
                          scorableRun && isScorable ? scorableRun : undefined
                        }
                      />
                      {/* ✅ Use getDisplayTimestamp instead of message.createdAt */}
                      {!isRunning && getDisplayTimestamp(message) && (
                        <div className="text-[10px] text-muted-foreground/60 mt-1">
                          {formatTimestamp(getDisplayTimestamp(message))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Make sure the child assistant messages of a step are displayed at the root level. */}
        {message.steps && isStep ? (
          <Messages
            messages={message.steps.filter((s) => s.type.includes('message'))}
            elements={elements}
            actions={actions}
            indent={0}
            isRunning={isRunning}
            scorableRun={scorableRun}
          />
        ) : null}
        {/* Display the child steps if the message is not a step (usually a user message). */}
        {message.steps && !isStep ? (
          <Messages
            messages={message.steps}
            elements={elements}
            actions={actions}
            indent={indent}
            isRunning={isRunning}
          />
        ) : null}
      </>
    );
  }
);

export { Message };
